import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Bot, ToggleLeft, ToggleRight, Link2, Settings } from 'lucide-react';
import axios from 'axios';
import { getSectors } from '../services/api';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function AIAgents() {
  const [agents, setAgents] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '',
    provider: 'astra',
    webhook_url: '',
    status: 'inactive',
    event_types: ['message_received', 'chat_assigned'],
    config: {},
    sector_id: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [agentsRes, sectorsRes] = await Promise.all([
        axios.get(`${API}/api/ai-agents`),
        getSectors()
      ]);
      setAgents(agentsRes.data);
      setSectors(sectorsRes.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`${API}/api/ai-agents/${editing.id}`, form);
      } else {
        await axios.post(`${API}/api/ai-agents`, form);
      }
      setShowModal(false);
      setEditing(null);
      setForm({
        name: '',
        provider: 'astra',
        webhook_url: '',
        status: 'inactive',
        event_types: ['message_received', 'chat_assigned'],
        config: {},
        sector_id: ''
      });
      loadData();
    } catch (err) {
      alert('Erro: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleEdit = (agent) => {
    setEditing(agent);
    setForm({
      name: agent.name,
      provider: agent.provider,
      webhook_url: agent.webhook_url || '',
      status: agent.status,
      event_types: agent.event_types || [],
      config: agent.config || {},
      sector_id: agent.sector_id || ''
    });
    setShowModal(true);
  };

  const toggleStatus = async (agent) => {
    try {
      await axios.put(`${API}/api/ai-agents/${agent.id}`, {
        status: agent.status === 'active' ? 'inactive' : 'active'
      });
      loadData();
    } catch (err) {
      alert('Erro ao atualizar status');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Excluir este agente de IA?')) {
      try {
        await axios.delete(`${API}/api/ai-agents/${id}`);
        loadData();
      } catch (err) {
        alert('Erro ao excluir');
      }
    }
  };

  const testConnection = async (agent) => {
    try {
      await axios.post(`${API}/api/ai-agents/${agent.id}/test`);
      alert('Conexão testada com sucesso!');
    } catch (err) {
      alert('Erro na conexão: ' + (err.response?.data?.error || err.message));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Agentes de IA Personalizados</h1>
          <p className="text-gray-500">Crie e integre agentes de IA para automatizar atendimentos</p>
        </div>
        <button 
          onClick={() => { setEditing(null); setShowModal(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 shadow-lg shadow-blue-100">
          <Plus size={18} /> Conectar Agente
        </button>
      </div>

      {/* Info Box */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-2xl p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-white rounded-xl">
            <Bot size={32} className="text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-gray-900 mb-1">Integração com Agentes de IA</h3>
            <p className="text-sm text-gray-600 mb-3">
              Conecte agentes de IA como Astra, Dialogflow ou soluções personalizadas para automatizar respostas, 
              qualificar leads e rotear conversas automaticamente.
            </p>
            <div className="flex gap-2">
              <span className="px-3 py-1 bg-white rounded-full text-xs font-medium text-gray-700">Astra AI</span>
              <span className="px-3 py-1 bg-white rounded-full text-xs font-medium text-gray-700">Dialogflow</span>
              <span className="px-3 py-1 bg-white rounded-full text-xs font-medium text-gray-700">Custom Webhook</span>
            </div>
          </div>
        </div>
      </div>

      {/* Agents List */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">Agentes Conectados</h2>
        </div>
        
        <div className="divide-y divide-gray-100">
          {agents.map(agent => (
            <div key={agent.id} className="p-6 hover:bg-gray-50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 flex-1">
                  <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center">
                    <Bot size={24} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-bold text-gray-900">{agent.name}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                        agent.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {agent.status === 'active' ? 'Habilitado' : 'Desabilitado'}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Settings size={14} />
                        {agent.provider}
                      </span>
                      {agent.sector_name && (
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                          {agent.sector_name}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Link2 size={14} />
                        {agent.event_types?.length || 0} eventos
                      </span>
                    </div>
                    {agent.webhook_url && (
                      <p className="text-xs text-gray-400 mt-2 truncate max-w-md">
                        {agent.webhook_url}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => testConnection(agent)}
                    className="px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg font-medium">
                    Testar Conexão
                  </button>
                  <button 
                    onClick={() => toggleStatus(agent)}
                    className={`p-2 rounded-lg ${
                      agent.status === 'active' ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'
                    }`}>
                    {agent.status === 'active' ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
                  </button>
                  <button 
                    onClick={() => handleEdit(agent)}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                    <Edit2 size={18} />
                  </button>
                  <button 
                    onClick={() => handleDelete(agent.id)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
          
          {agents.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <Bot size={48} className="mx-auto mb-3 opacity-50" />
              <p>Nenhum agente de IA conectado</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-2xl p-6 shadow-xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-bold mb-4">{editing ? 'Editar Agente' : 'Conectar Agente de IA'}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Agente *</label>
                <input 
                  type="text" 
                  placeholder="Ex: Astra - IA Setor Médico" 
                  value={form.name} 
                  onChange={e => setForm({...form, name: e.target.value})} 
                  required
                  className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none" 
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Provedor</label>
                  <select 
                    value={form.provider} 
                    onChange={e => setForm({...form, provider: e.target.value})}
                    className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none">
                    <option value="astra">Astra AI</option>
                    <option value="dialogflow">Dialogflow</option>
                    <option value="custom">Webhook Customizado</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Setor</label>
                  <select 
                    value={form.sector_id} 
                    onChange={e => setForm({...form, sector_id: e.target.value})}
                    className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none">
                    <option value="">Todos os setores</option>
                    {sectors.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL do Webhook *</label>
                <input 
                  type="url" 
                  placeholder="https://..." 
                  value={form.webhook_url} 
                  onChange={e => setForm({...form, webhook_url: e.target.value})} 
                  required
                  className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none" 
                />
                <p className="text-xs text-gray-500 mt-1">URL para recebimento de eventos do WhatsApp</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tipos de Eventos</label>
                <div className="space-y-2">
                  {[
                    { value: 'message_received', label: 'Mensagem Atribuída Recebida' },
                    { value: 'chat_assigned', label: 'Chat Atribuído' },
                    { value: 'chat_closed', label: 'Chat Fechado' }
                  ].map(event => (
                    <label key={event.value} className="flex items-center gap-2 p-2 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                      <input 
                        type="checkbox"
                        checked={form.event_types.includes(event.value)}
                        onChange={e => {
                          if (e.target.checked) {
                            setForm({...form, event_types: [...form.event_types, event.value]});
                          } else {
                            setForm({...form, event_types: form.event_types.filter(t => t !== event.value)});
                          }
                        }}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{event.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-4 border-t border-gray-100">
                <button 
                  type="button" 
                  onClick={() => setShowModal(false)} 
                  className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50">
                  Cancelar
                </button>
                <button 
                  type="submit" 
                  className="px-5 py-2 bg-blue-600 text-white rounded-xl text-sm font-bold hover:bg-blue-700">
                  Conectar Agente
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AIAgents;